import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll, useMotionTemplate } from "framer-motion";
import { ArrowDownRight, Repeat2 } from "lucide-react";
import { scrollToId, openRequest, eur, imgSrc } from "@/lib/api";
import { useSettings } from "@/lib/settings";

const EASE = [0.16, 1, 0.3, 1];
const LINE_CLS = ["text-fg", "text-outline-volt italic", "text-fg"];
const FALLBACK = { name: "Apex Pro 15", price: 1199, image: "/img/apex.jpg" };

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%", rotate: 4 }} animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.2, delay: 0.35 + i * 0.13, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const PhoneStage = ({ mx, my, product }) => {
  const rx = useSpring(useTransform(my, [0, 1], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-14, 14]), { stiffness: 120, damping: 18 });
  const gx = useTransform(mx, (v) => `${v * 100}%`);
  const gy = useTransform(my, (v) => `${v * 100}%`);
  const spot = useMotionTemplate`radial-gradient(420px circle at ${gx} ${gy}, rgba(0,240,255,0.28), transparent 60%)`;

  return (
    <div className="relative mx-auto w-full max-w-[420px]" style={{ perspective: 1200 }}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div initial={{ clipPath: "inset(100% 0% 0% 0% round 2.5rem)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 2.5rem)" }} transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
          className="relative aspect-[4/5] overflow-hidden rounded-[2.5rem] border border-fg/10 bg-[var(--ink-2)]">
          <motion.img key={product.image} src={imgSrc(product.image)} alt={product.name} data-testid="hero-featured-image" initial={{ scale: 1.3 }} animate={{ scale: 1 }}
            transition={{ duration: 1.8, delay: 0.5, ease: EASE }} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <motion.div className="absolute inset-0 mix-blend-screen" style={{ background: spot }} />
        </motion.div>
        <motion.div style={{ transform: "translateZ(80px)" }} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute -left-4 top-10 rounded-2xl border border-fg/10 bg-[#f3f3f4]/85 px-4 py-3 backdrop-blur-xl sm:-left-10">
          <p className="font-mono-ui text-[11px] uppercase tracking-widest text-mist">In evidenza</p>
          <p className="font-display text-lg" data-testid="hero-featured-name">{product.name}</p>
        </motion.div>
        <motion.div style={{ transform: "translateZ(120px)" }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.7, duration: 0.8 }}
          className="absolute -right-2 bottom-12 rounded-2xl bg-[var(--volt)] px-4 py-3 text-[var(--ink)] sm:-right-8">
          <p className="font-mono-ui text-[11px] uppercase tracking-widest">da</p>
          <p className="font-display text-2xl font-semibold" data-testid="hero-featured-price">{eur(product.price)}</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { settings } = useSettings();
  const lines = [settings.hero_line1, settings.hero_line2, settings.hero_line3];
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const stageY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden pt-32 sm:pt-36">
      <div className="grid-lines absolute inset-0" />
      <motion.p style={{ y: bgY }} aria-hidden
        className="pointer-events-none absolute -bottom-6 left-0 select-none whitespace-nowrap font-display text-[24vw] font-bold leading-none text-outline opacity-40">
        SmartX
      </motion.p>
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="mb-8 flex items-center gap-3 font-mono-ui text-xs uppercase tracking-[0.3em] text-mist">
            <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--volt)]" /> Smartphone · Audio · Accessori
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[3.4rem] font-semibold leading-[0.92] tracking-tight sm:text-7xl lg:text-[6.5rem]">
            {lines.map((t, i) => <MaskLine key={`${i}-${t}`} i={i} className={LINE_CLS[i]}>{t}</MaskLine>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-md text-base text-fg2 md:text-lg" data-testid="hero-subtitle">
            {settings.hero_subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-3">
            <button data-testid="hero-primary-cta" onClick={() => scrollToId("catalogo")}
              className="shine group flex items-center gap-3 rounded-full bg-[var(--volt)] px-7 py-4 font-semibold text-[var(--ink)] transition-transform active:scale-95">
              Esplora il catalogo <ArrowDownRight className="h-5 w-5 transition-transform group-hover:rotate-[-45deg]" />
            </button>
            <button data-testid="hero-secondary-cta" onClick={() => openRequest("permuta")}
              className="flex items-center gap-3 rounded-full border border-fg/20 px-7 py-4 text-fg transition-colors hover:border-fg/40 hover:bg-fg/5 active:scale-95">
              <Repeat2 className="h-5 w-5" /> Valuta il tuo usato
            </button>
          </motion.div>
        </div>
        <motion.div style={{ y: stageY }} className="pb-20 lg:col-span-5 lg:pb-0">
          <PhoneStage mx={mx} my={my} product={settings.featured || FALLBACK} />
        </motion.div>
      </div>
    </section>
  );
};
